import type {DropResult} from '@hello-pangea/dnd';
import {Menu} from '@mantine/core';
import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {IconAlertCircle, IconAlertTriangle} from '@tabler/icons-react';
import React, {useMemo} from 'react';
import type {CourseDefinitionContentResponse} from 'src/api/ProactApiClient';
import type {CourseDefinitionContentTypeEnum} from 'src/api/ProactApiClient';
import {CreateButton} from 'src/design/molecules/CreateButton';
import {DraggableList} from 'src/design/molecules/DraggableList';
import type {DraggableListItemContent} from 'src/design/molecules/DraggableListItem';
import {renderContentHeaderText} from 'src/app/training/courseContentValidation';
import {contentTypeLabel, contentTypes} from './contentTypes';

export const contentSelectionKey = (
  content: CourseDefinitionContentResponse
): string => content.invariantId!;

export interface CourseOutlineProps {
  contents: CourseDefinitionContentResponse[];
  invalidInvariantIds: Set<string>;
  selectedKey?: string;
  disabled?: boolean;
  onSelect: (key: string) => void;
  onAddContent: (type: CourseDefinitionContentTypeEnum) => void;
  onDeleteContent: (invariantId: string) => void;
  onReorderContents: (contents: CourseDefinitionContentResponse[]) => void;
}

/**
 * Column 1 of the Build canvas: the trainee-facing content sequence, and
 * nothing else. Course settings live in the header section above the canvas,
 * and a quiz's questions live inline in the editor — so every node here is a
 * peer that can be reordered and deleted.
 *
 * Selection is parent-controlled and keyed by `invariantId` — never by array
 * position — so reordering never changes what's selected, and adding an item
 * always selects the new item regardless of where it lands.
 */
export const CourseOutline: React.FC<CourseOutlineProps> = ({
  contents,
  invalidInvariantIds,
  selectedKey,
  disabled,
  onSelect,
  onAddContent,
  onDeleteContent,
  onReorderContents,
}) => {
  const contentItems = useMemo<DraggableListItemContent[]>(
    () =>
      contents.map(content => ({
        id: contentSelectionKey(content),
        primary: renderContentHeaderText(content),
        primaryIcon: invalidInvariantIds.has(content.invariantId!) ? (
          <IconAlertTriangle
            size={14}
            color="var(--mantine-color-yellow-6)"
            aria-label="Validation warning"
          />
        ) : undefined,
        secondary: contentTypeLabel(content.type),
      })),
    [contents, invalidInvariantIds]
  );

  const selectedContentIndex = contents.findIndex(
    content => contentSelectionKey(content) === selectedKey
  );

  const handleContentDragEnd = (result: DropResult) => {
    const {destination, source} = result;
    if (!destination || destination.index === source.index) return;

    const items = Array.from(contents);
    const [removed] = items.splice(source.index, 1);
    items.splice(destination.index, 0, removed);
    onReorderContents(items);
  };

  return (
    <Stack gap="xs">
      {contents.length === 0 && (
        <Text size="sm" c="dimmed">
          This course has no content yet.
        </Text>
      )}

      <DraggableList
        items={contentItems}
        selectedIndex={
          selectedContentIndex >= 0 ? selectedContentIndex : undefined
        }
        disabled={disabled}
        onDragEnd={handleContentDragEnd}
        onSelect={onSelect}
        onDelete={onDeleteContent}
      />

      <Group justify="center">
        <Menu shadow="md" width={200} position="bottom" withinPortal>
          <Menu.Target>
            {/* Menu.Target clones its child with a ref; CreateButton is not a
                forwardRef component, so it is wrapped in an element that is. */}
            <div style={{display: 'inline-flex'}}>
              <CreateButton size="sm" disabled={disabled}>
                Add Content
              </CreateButton>
            </div>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Label>Content type</Menu.Label>
            {contentTypes.map(type => (
              <Menu.Item key={type} onClick={() => onAddContent(type)}>
                {contentTypeLabel(type)}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      </Group>

      {invalidInvariantIds.size > 0 && (
        <Group gap={4} c="red" wrap="nowrap">
          <IconAlertCircle size={14} />
          <Text size="xs" c="red">
            Items with a warning icon have validation errors.
          </Text>
        </Group>
      )}
    </Stack>
  );
};
